<template>
    <section class="guides-list">
        <div class="slice-content">
            <div class="content">
                <PrismicRichText :field="slice.primary.title" class="title" />
                <PrismicRichText :field="slice.primary.headline" class="headline" />
            </div>
            <div class="guides">
                <div class="content">
                    <div class="guides-grid">
                        <GuideCard v-for="guide in guides" :key="guide.uid" :guide="guide" />
                    </div>
                    <div class="button-row">
                        <NuxtLink v-slot="{href, navigate}" :to="$withLocalePrefix('/resources/guides')" custom>
                            <VJoyButton variant="main" :href="href" @click="navigate">{{ t('resources.guides.seeAll') }}</VJoyButton>
                        </NuxtLink>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {isFilled} from '@prismicio/helpers';
    import type {FilledContentRelationshipField} from '@prismicio/types';
    import {PrismicRichText} from '@prismicio/vue';
    import type {GuideDocument, GuideDocumentData, GuidesListSliceDefault} from '@prismicio-types';
    import {useAsyncData, useTranslation} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import GuideCard from '~/components/GuideCard.vue';
    import usePrismicSearch from '~/composables/content/usePrismicSearch';

    const props = defineProps<{slice: GuidesListSliceDefault}>();
    const guides = [props.slice.primary.guide1, props.slice.primary.guide2, props.slice.primary.guide3].filter((article) =>
        isFilled.contentRelationship<'guide', string, GuideDocumentData>(article),
    ) as (FilledContentRelationshipField<'guide', string, GuideDocumentData> | GuideDocument)[];

    const {t} = useTranslation();

    function fetchGuides() {
        if (guides.length < 3) {
            // prismic does not allow 'or' queries, so we have to do multiple queries
            const searchQueries = props.slice.items
                .filter((searchFilter) => searchFilter.categoryFilter)
                .map((item) =>
                    usePrismicSearch({
                        type: 'guide',
                        pageSize: 3 - guides.length,
                        category: item.categoryFilter ?? undefined,
                        excludes: guides.map((guide) => guide.id),
                    }),
                );
            if (searchQueries.length === 0) {
                searchQueries.push(
                    usePrismicSearch({
                        type: 'guide',
                        pageSize: 3 - guides.length,
                        excludes: guides.map((guide) => guide.id),
                    }),
                );
            }
            return Promise.all(searchQueries);
        }
        return Promise.resolve([]);
    }

    const {data: complementaryGuides} = await useAsyncData('complementary-guides', fetchGuides, {deep: true});

    if (complementaryGuides.value) {
        const mostRecentComplementaryGuides = complementaryGuides.value
            .map((query) => query?.results)
            .flat()
            .filter((guide) => guide !== undefined)
            .sort((a, b) => new Date(b?.data?.releaseDate).getTime() - new Date(a?.data?.releaseDate).getTime())
            .slice(0, 3 - guides.length) as GuideDocument[];
        guides.push(...mostRecentComplementaryGuides);
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .guides-list .slice-content {
        text-align: left;
    }

    .guides-list {
        background-color: var(--joy-color-background-default);
        position: relative;

        .guides {
            .guides-grid {
                display: grid;
                row-gap: var(--joy-core-spacing-5);
                grid-template-columns: minmax(0, 1fr);
                z-index: 1;
                position: relative;

                @include mq.screen_sm(min) {
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    column-gap: var(--joy-core-spacing-3);
                    row-gap: var(--joy-core-spacing-4);

                    & > :last-child {
                        grid-column: 1 / -1;
                        max-width: 50%;
                        margin: 0 auto;
                    }
                }
                @include mq.screen_lg(min) {
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    column-gap: var(--joy-core-spacing-4);
                    row-gap: var(--joy-core-spacing-6);
                    margin-bottom: 70px;

                    & > :last-child {
                        grid-column: auto;
                        max-width: 100%;
                    }
                }
            }
        }

        .content {
            .title p {
                text-align: center;
                color: var(--joy-color-primary-50);
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-secondary-200);
                margin-bottom: var(--joy-core-spacing-5);
            }

            .headline {
                text-align: center;
                margin-bottom: var(--joy-core-spacing-11);
            }

            .button-row {
                display: flex;
                justify-content: center;
                margin-top: var(--joy-core-spacing-12);

                @include mq.screen_sm(min) {
                    margin-top: 80px;
                }
            }
        }
    }
</style>
